import type { AudioLanguage } from "./types";
import { assertNever } from "@/packages/ts-utils";
import type {
  ActiveDeploymentRegion,
  DeploymentRegion,
} from "@/utils/deployment";
import { buildSupabaseURL } from "@/utils/helpers";
import type {
  OutputConfig,
  ThinkingConfigParam,
} from "@anthropic-ai/sdk/resources";

export enum RESOURCE_NAME {
  LISTENLABS_GPT_4_ENTERPRISE = "listenlabs-gpt-4-enterprise",
  LISTENLABS_COGNITIVE_SERVICES = "listenlabs",
  LISTEN_MAIN = "listen-main-resource",
  MERLIN_GPT_4 = "merlin-gpt4",
}

export enum API_KEY_NAME {
  AZURE_OPENAI_KEY = "AZURE_OPENAI_KEY",
  AZURE_OPENAI_KEY_EAST_US_LISTEN_LABS = "AZURE_OPENAI_KEY_EAST_US_LISTEN_LABS",
  AZURE_OPENAI_KEY_EAST_US_2_LISTEN_LABS_ENTERPRISE = "AZURE_OPENAI_KEY_EAST_US_2_LISTEN_LABS_ENTERPRISE",
  AZURE_OPENAI_KEY_LISTEN_MAIN = "AZURE_OPENAI_KEY_LISTEN_MAIN",
}

export enum API_VERSION {
  API_VERSION_2025_04_01_PREVIEW = "2025-04-01-preview",
  API_VERSION_2025_01_01_PREVIEW = "2025-01-01-preview",
  API_VERSION_2024_12_01_PREVIEW = "2024-12-01-preview",
}

export const ASSEMBLY_AI_STREAMING_URL = "https://streaming.assemblyai.com/v3";

export type AZURE_DEPLOYMENT_CONFIG = {
  resourceName: RESOURCE_NAME;
  apiKeyName: API_KEY_NAME;
  apiVersion: API_VERSION;
};

export const AZURE_DEPLOYMENT_URL_BASE =
  "https://{{resourceName}}.openai.azure.com/openai/deployments/{{model}}/chat/completions?api-version={{apiVersion}}";

// To add a new deployment, add the config to this array, and map the model to the config in OPENAI_MODEL_TO_AZURE_DEPLOYMENT
export const AZURE_DEPLOYMENT_CONFIGS: AZURE_DEPLOYMENT_CONFIG[] = [
  {
    resourceName: RESOURCE_NAME.LISTENLABS_GPT_4_ENTERPRISE,
    apiKeyName: API_KEY_NAME.AZURE_OPENAI_KEY_EAST_US_2_LISTEN_LABS_ENTERPRISE,
    apiVersion: API_VERSION.API_VERSION_2025_01_01_PREVIEW,
  },
  {
    resourceName: RESOURCE_NAME.LISTENLABS_COGNITIVE_SERVICES,
    apiKeyName: API_KEY_NAME.AZURE_OPENAI_KEY_EAST_US_LISTEN_LABS,
    apiVersion: API_VERSION.API_VERSION_2025_01_01_PREVIEW,
  },
  {
    resourceName: RESOURCE_NAME.MERLIN_GPT_4,
    apiKeyName: API_KEY_NAME.AZURE_OPENAI_KEY,
    apiVersion: API_VERSION.API_VERSION_2024_12_01_PREVIEW,
  },
  {
    resourceName: RESOURCE_NAME.LISTEN_MAIN,
    apiKeyName: API_KEY_NAME.AZURE_OPENAI_KEY_LISTEN_MAIN,
    apiVersion: API_VERSION.API_VERSION_2025_04_01_PREVIEW,
  },
];

export const CEREBRAS_MODELS = ["cerebras_gpt-oss-120b"] as const;

export const OPENAI_MODELS = [
  "o3-2025-04-16",
  "o4-mini-2025-04-16",
  "gpt-4.1-2025-04-14",
  "gpt-4.1-mini-2025-04-14",
  "gpt-4.1-nano-2025-04-14",
  "gpt-4o-2024-08-06",
  "gpt-4o-2024-05-13",
  "gpt-4-turbo-2024-04-09",
  "gpt-4-turbo", // by default gpt-4 is already turbo but not for azure
  "gpt-4",
  "gpt-4-32k",
  "gpt-3.5",
  "gpt-3.5-16k",
  "gpt-4o-mini-2024-07-18",
  "gpt-5-2025-08-07",
  "gpt-5-mini-2025-08-07",
  "gpt-5-nano-2025-08-07",
  "gpt-5.5",
  "gpt-5.4-2026-03-05",
  "gpt-5.4-mini-2026-03-17",
  "gpt-5.4-nano-2026-03-17",
  "gpt-5.6-luna",
  "gpt-5.6-sol",
  "gpt-5.6-terra",
  "gpt-6-astra",
  "gpt-6.1-sol",
  "gpt-6-luna",
] as const;

export const ANTHROPIC_MODELS = [
  "claude-sonnet-5",
  "claude-sonnet-5-5",
  "claude-haiku-5-5",
  "claude-sonnet-4-5-20250929",
  "claude-sonnet-4-6",
  "claude-opus-4-5-20251101",
  "claude-opus-4-6",
  "claude-opus-4-7",
  "claude-opus-4-8",
  "claude-opus-5",
  "claude-opus-5-5",
  "claude-fable-5",
  "claude-fable-5-1",
] as const;

// Gemini equivalents of open ai's low, medium, and high thinking budgets
// Gemini 3 now supports the low/medium/high thinking budgets but langchain does not
// TODO(future): remove once langchain supports more basic thinking budgets
export const GEMINI_THINKING_BUDGET_LOW = 1024;
export const GEMINI_THINKING_BUDGET_MEDIUM = 8192;
export const GEMINI_THINKING_BUDGET_HIGH = 24576;

export const GEMINI_EXTERNAL_URL_MAX_BYTES = 15 * 1024 * 1024;

export const GEMINI_MODELS = [
  "gemini-2.5-flash",
  "gemini-2.5-pro",
  "gemini-3.1-pro-preview",
  "gemini-3-flash-preview",
  "gemini-3.5-flash",
  "gemini-3.1-flash-lite",
  "gemini-3.5-flash-lite",
  "gemini-3.6-flash",
  "gemini-3.8-flash",
  "gemini-4-argon",
] as const;

export const DEPRECATED_ANTHROPIC_MODELS = [
  "claude-3-7-sonnet-20250219",
  "claude-3-5-sonnet-20241022",
  "claude-3-5-sonnet-20240620",
  "claude-3-opus-20240229",
  "claude-3-sonnet-20240229",
  "claude-3-haiku-20240307",
  "claude-sonnet-4-20250514",
  "claude-opus-4-20250514",
] as const;
export type DEPRECATED_ANTHROPIC_MODEL =
  (typeof DEPRECATED_ANTHROPIC_MODELS)[number];
export const DEPRECATED_ANTHROPIC_MODELS_MAP: {
  [K in DEPRECATED_ANTHROPIC_MODEL]: ANTHROPIC_MODEL;
} = {
  "claude-3-7-sonnet-20250219": "claude-sonnet-4-5-20250929",
  "claude-3-5-sonnet-20240620": "claude-sonnet-4-5-20250929",
  "claude-3-5-sonnet-20241022": "claude-sonnet-4-5-20250929",
  "claude-3-opus-20240229": "claude-opus-4-5-20251101",
  "claude-3-sonnet-20240229": "claude-sonnet-4-5-20250929",
  "claude-3-haiku-20240307": "claude-sonnet-4-5-20250929",
  "claude-sonnet-4-20250514": "claude-sonnet-4-5-20250929",
  "claude-opus-4-20250514": "claude-opus-4-5-20251101",
};
export type OPENAI_MODEL = (typeof OPENAI_MODELS)[number];
export type ANTHROPIC_MODEL = (typeof ANTHROPIC_MODELS)[number];
export type GEMINI_MODEL = (typeof GEMINI_MODELS)[number];
export type CEREBRAS_MODEL = (typeof CEREBRAS_MODELS)[number];

// These models reject manual thinking budgets and non-default temperature
// with a 400.
export const ANTHROPIC_FIXED_SAMPLING_MODELS: ReadonlyArray<ANTHROPIC_MODEL> = [
  "claude-sonnet-5",
  "claude-sonnet-5-5",
  "claude-haiku-5-5",
  "claude-opus-4-7",
  "claude-opus-4-8",
  "claude-opus-5",
  "claude-opus-5-5",
  "claude-fable-5",
  "claude-fable-5-1",
];

export const ANTHROPIC_ADAPTIVE_THINKING_ONLY_MODELS: ReadonlyArray<ANTHROPIC_MODEL> =
  [
    "claude-sonnet-5",
    "claude-sonnet-5-5",
    "claude-haiku-5-5",
    "claude-opus-4-7",
    "claude-opus-4-8",
    "claude-opus-5",
    "claude-opus-5-5",
    "claude-fable-5",
    "claude-fable-5-1",
  ];

// These models reject disabled thinking, so they always get adaptive.
export const ANTHROPIC_ALWAYS_THINKING_MODELS: ReadonlyArray<ANTHROPIC_MODEL> =
  [
    "claude-sonnet-5-5",
    "claude-opus-5-5",
    "claude-fable-5",
    "claude-fable-5-1",
  ];

export const normalizeAnthropicThinking = ({
  model,
  thinking,
}: {
  model: ANTHROPIC_MODEL;
  thinking?: ThinkingConfigParam;
}): ThinkingConfigParam => {
  if (ANTHROPIC_ALWAYS_THINKING_MODELS.includes(model)) {
    return { type: "adaptive" };
  }
  if (
    thinking?.type === "enabled" &&
    ANTHROPIC_ADAPTIVE_THINKING_ONLY_MODELS.includes(model)
  ) {
    return { type: "adaptive" };
  }
  return thinking ?? { type: "disabled" };
};

export const normalizeAnthropicTemperature = ({
  model,
  temperature,
}: {
  model: ANTHROPIC_MODEL;
  temperature?: number;
}): number | undefined =>
  ANTHROPIC_FIXED_SAMPLING_MODELS.includes(model) ? undefined : temperature;

// The inverse constraint: older models reject outputConfig.effort with a 400.
export const ANTHROPIC_NO_EFFORT_MODELS: ReadonlyArray<ANTHROPIC_MODEL> = [
  "claude-sonnet-4-5-20250929",
];

export const normalizeAnthropicOutputConfig = ({
  model,
  outputConfig,
}: {
  model: ANTHROPIC_MODEL;
  outputConfig?: OutputConfig;
}): OutputConfig | undefined => {
  if (!outputConfig?.effort || !ANTHROPIC_NO_EFFORT_MODELS.includes(model)) {
    return outputConfig;
  }
  const { effort: _effort, ...rest } = outputConfig;
  return Object.keys(rest).length > 0 ? rest : undefined;
};

export const GEMINI_PRIORITY_QUEUE_MODELS: ReadonlyArray<GEMINI_MODEL> = [
  "gemini-3.1-pro-preview",
  "gemini-3-flash-preview",
];

export const isGeminiPriorityCandidateModel = (model: GEMINI_MODEL): boolean =>
  GEMINI_PRIORITY_QUEUE_MODELS.includes(model);

export type LLM_MODEL =
  | OPENAI_MODEL
  | ANTHROPIC_MODEL
  | GEMINI_MODEL
  | CEREBRAS_MODEL;

// LLM_MODEL minus the providers the interview streaming path doesn't handle.
export type STREAMABLE_MODEL = Exclude<LLM_MODEL, CEREBRAS_MODEL>;

export const isStreamableInterviewerModel = (
  value: unknown
): value is STREAMABLE_MODEL =>
  typeof value === "string" &&
  ((ANTHROPIC_MODELS as readonly string[]).includes(value) ||
    (OPENAI_MODELS as readonly string[]).includes(value) ||
    (GEMINI_MODELS as readonly string[]).includes(value));

export type DEPRECATED_LLM_MODEL = DEPRECATED_ANTHROPIC_MODEL;
export const DEPRECATED_LLM_MODELS_MAP: {
  [K in DEPRECATED_LLM_MODEL]: LLM_MODEL;
} = {
  ...DEPRECATED_ANTHROPIC_MODELS_MAP,
};
export const DEPRECATED_LLM_MODELS: DEPRECATED_LLM_MODEL[] = [
  ...DEPRECATED_ANTHROPIC_MODELS,
] as const;

function hasModel<P extends { model: string }, M extends P["model"]>(
  pkg: P,
  ...models: M[]
): pkg is Extract<P, { model: M }> {
  return (models as string[]).includes(pkg.model);
}

export function isOpenAICallProps<P extends { model: string }>(props: P) {
  // : props is Extract<P, { model: OPENAI_MODEL }> {
  return hasModel(props, ...OPENAI_MODELS);
}
export function isAnthropicCallProps<P extends { model: string }>(props: P) {
  // : props is Extract<P, { model: ANTHROPIC_MODEL }> {
  return hasModel(props, ...ANTHROPIC_MODELS);
}
export function isGeminiCallProps<P extends { model: string }>(props: P) {
  // : props is Extract<P, { model: GEMINI_MODEL }> {
  return hasModel(props, ...GEMINI_MODELS);
}
export function isCerebrasCallProps<P extends { model: string }>(props: P) {
  // : props is Extract<P, { model: CEREBRAS_MODEL }> {
  return hasModel(props, ...CEREBRAS_MODELS);
}
export function isOpenAiModel(model: LLM_MODEL): model is OPENAI_MODEL {
  return OPENAI_MODELS.includes(model as OPENAI_MODEL);
}
export function isAnthropicModel(model: LLM_MODEL): model is ANTHROPIC_MODEL {
  return ANTHROPIC_MODELS.includes(model as ANTHROPIC_MODEL);
}
export function isGeminiModel(model: LLM_MODEL): model is GEMINI_MODEL {
  return GEMINI_MODELS.includes(model as GEMINI_MODEL);
}
export function isCerebrasModel(model: LLM_MODEL): model is CEREBRAS_MODEL {
  return CEREBRAS_MODELS.includes(model as CEREBRAS_MODEL);
}
export function isDeprecatedModel(
  model: LLM_MODEL | DEPRECATED_LLM_MODEL
): model is DEPRECATED_LLM_MODEL {
  return DEPRECATED_LLM_MODELS.includes(model as DEPRECATED_LLM_MODEL);
}

// Map config/legacy models to interview-streamable ones; undefined → use the flag.
export function toStreamableInterviewerModel(
  model: LLM_MODEL | DEPRECATED_LLM_MODEL | undefined
): STREAMABLE_MODEL | undefined {
  if (!model) return undefined;
  const resolved = isDeprecatedModel(model)
    ? DEPRECATED_LLM_MODELS_MAP[model]
    : model;
  return isStreamableInterviewerModel(resolved) ? resolved : undefined;
}

export const LLM_PROVIDERS = [
  "openai",
  "anthropic",
  "gemini",
  "cerebras",
] as const;
export type LLM_PROVIDER = (typeof LLM_PROVIDERS)[number];
export function getLLMProvider(model: LLM_MODEL): LLM_PROVIDER {
  if (isOpenAiModel(model)) return "openai";
  if (isAnthropicModel(model)) return "anthropic";
  if (isGeminiModel(model)) return "gemini";
  if (isCerebrasModel(model)) return "cerebras";
  throw new Error("Unknown model");
}

export type LLM_PROVIDERS_WITH_INFERENCE_PROVIDER =
  | Exclude<LLM_PROVIDER, "anthropic" | "openai">
  | "anthropic_bedrock"
  | "anthropic_api"
  | "openai_azure"
  | "openai_api";

export const MODEL_NAMES_AZURE: Record<OPENAI_MODEL, string> = {
  "gpt-5-2025-08-07": "gpt-5-2025-08-07",
  "gpt-5-mini-2025-08-07": "gpt-5-mini-2025-08-07",
  "gpt-5-nano-2025-08-07": "gpt-5-nano-2025-08-07",
  "gpt-5.5": "gpt-5.5",
  "gpt-5.4-2026-03-05": "gpt-5.4-2026-03-05",
  "gpt-5.4-mini-2026-03-17": "gpt-5.4-mini-2026-03-17",
  "gpt-5.4-nano-2026-03-17": "gpt-5.4-nano-2026-03-17",
  "gpt-5.6-luna": "gpt-5.6-luna",
  "gpt-5.6-sol": "gpt-5.6-sol",
  "gpt-5.6-terra": "gpt-5.6-terra",
  "gpt-6-astra": "gpt-6-astra",
  "gpt-6.1-sol": "gpt-6.1-sol",
  "gpt-6-luna": "gpt-6-luna",
  "o3-2025-04-16": "o3-2025-04-16",
  "o4-mini-2025-04-16": "o4-mini-2025-04-16",
  "gpt-4.1-2025-04-14": "gpt-4.1-2025-04-14",
  "gpt-4.1-mini-2025-04-14": "gpt-4.1-mini-2025-04-14",
  "gpt-4.1-nano-2025-04-14": "gpt-4.1-nano-2025-04-14",
  "gpt-4o-2024-08-06": "gpt-4o-2024-08-06",
  "gpt-4o-2024-05-13": "gpt-4o",
  "gpt-4-turbo-2024-04-09": "gpt-4-turbo",
  "gpt-4-turbo": "gpt-4-turbo",
  "gpt-4": "gpt-4",
  "gpt-4-32k": "gpt-4-32k",
  "gpt-3.5": "gpt-35-turbo",
  "gpt-3.5-16k": "gpt-35-turbo",
  "gpt-4o-mini-2024-07-18": "gpt-4o-mini", // TODO(future): map to correct version
};

export const ANTHROPIC_MAX_TOKENS = 16000;

export const OPENAI_MODEL_TO_AZURE_DEPLOYMENT: Record<
  OPENAI_MODEL,
  AZURE_DEPLOYMENT_CONFIG
> = {
  "gpt-5-2025-08-07": AZURE_DEPLOYMENT_CONFIGS[0],
  "gpt-5-mini-2025-08-07": AZURE_DEPLOYMENT_CONFIGS[0],
  "gpt-5-nano-2025-08-07": AZURE_DEPLOYMENT_CONFIGS[0],
  "gpt-5.5": AZURE_DEPLOYMENT_CONFIGS[3],
  "gpt-5.4-2026-03-05": AZURE_DEPLOYMENT_CONFIGS[3],
  "gpt-5.4-mini-2026-03-17": AZURE_DEPLOYMENT_CONFIGS[3],
  "gpt-5.4-nano-2026-03-17": AZURE_DEPLOYMENT_CONFIGS[3],
  "gpt-5.6-luna": AZURE_DEPLOYMENT_CONFIGS[3],
  "gpt-5.6-sol": AZURE_DEPLOYMENT_CONFIGS[3],
  "gpt-5.6-terra": AZURE_DEPLOYMENT_CONFIGS[3],
  "gpt-6-astra": AZURE_DEPLOYMENT_CONFIGS[3],
  "gpt-6.1-sol": AZURE_DEPLOYMENT_CONFIGS[3],
  "gpt-6-luna": AZURE_DEPLOYMENT_CONFIGS[3],
  "o3-2025-04-16": AZURE_DEPLOYMENT_CONFIGS[0],
  "o4-mini-2025-04-16": AZURE_DEPLOYMENT_CONFIGS[0],
  "gpt-4.1-2025-04-14": AZURE_DEPLOYMENT_CONFIGS[0],
  "gpt-4.1-mini-2025-04-14": AZURE_DEPLOYMENT_CONFIGS[0],
  "gpt-4.1-nano-2025-04-14": AZURE_DEPLOYMENT_CONFIGS[0],
  "gpt-4o-2024-08-06": AZURE_DEPLOYMENT_CONFIGS[1],
  "gpt-4o-2024-05-13": AZURE_DEPLOYMENT_CONFIGS[2],
  "gpt-4-turbo-2024-04-09": AZURE_DEPLOYMENT_CONFIGS[2],
  "gpt-4-turbo": AZURE_DEPLOYMENT_CONFIGS[2],
  "gpt-4": AZURE_DEPLOYMENT_CONFIGS[2],
  "gpt-4-32k": AZURE_DEPLOYMENT_CONFIGS[2],
  "gpt-3.5": AZURE_DEPLOYMENT_CONFIGS[2],
  "gpt-3.5-16k": AZURE_DEPLOYMENT_CONFIGS[2],
  "gpt-4o-mini-2024-07-18": AZURE_DEPLOYMENT_CONFIGS[2],
};

export const MODEL_NAMES_OPENAI: Record<OPENAI_MODEL, string> = {
  "o3-2025-04-16": "o3-2025-04-16",
  "o4-mini-2025-04-16": "o4-mini-2025-04-16",
  "gpt-4.1-2025-04-14": "gpt-4.1-2025-04-14",
  "gpt-4.1-mini-2025-04-14": "gpt-4.1-mini-2025-04-14",
  "gpt-4.1-nano-2025-04-14": "gpt-4.1-nano-2025-04-14",
  "gpt-4o-2024-08-06": "gpt-4o-2024-08-06",
  "gpt-4o-2024-05-13": "gpt-4o-2024-05-13",
  "gpt-4-turbo-2024-04-09": "gpt-4-turbo-2024-04-09",
  "gpt-4-turbo": "gpt-4-turbo-preview",
  "gpt-4": "gpt-4-1106-preview",
  "gpt-4-32k": "gpt-4-1106-preview",
  "gpt-3.5": "gpt-3.5-turbo-1106",
  "gpt-3.5-16k": "gpt-3.5-turbo-1106",
  "gpt-4o-mini-2024-07-18": "gpt-4o-mini-2024-07-18",
  "gpt-5-2025-08-07": "gpt-5-2025-08-07",
  "gpt-5-mini-2025-08-07": "gpt-5-mini-2025-08-07",
  "gpt-5-nano-2025-08-07": "gpt-5-nano-2025-08-07",
  "gpt-5.5": "gpt-5.5",
  "gpt-5.4-2026-03-05": "gpt-5.4-2026-03-05",
  "gpt-5.4-mini-2026-03-17": "gpt-5.4-mini-2026-03-17",
  "gpt-5.4-nano-2026-03-17": "gpt-5.4-nano-2026-03-17",
  "gpt-5.6-luna": "gpt-5.6-luna",
  "gpt-5.6-sol": "gpt-5.6-sol",
  "gpt-5.6-terra": "gpt-5.6-terra",
  "gpt-6-astra": "gpt-6-astra",
  "gpt-6.1-sol": "gpt-6.1-sol",
  "gpt-6-luna": "gpt-6-luna",
};

export const MODEL_NAMES_CEREBRAS: Record<CEREBRAS_MODEL, string> = {
  "cerebras_gpt-oss-120b": "gpt-oss-120b",
};

// A new ActiveDeploymentRegion fails here until it has a profile for every model.
// null means that region has no profile. The call fails instead of using another region.
export const MODEL_NAMES_BEDROCK = {
  us: {
    "claude-sonnet-5": "us.anthropic.claude-sonnet-5",
    "claude-sonnet-5-5": "us.anthropic.claude-sonnet-5-5",
    "claude-haiku-5-5": "us.anthropic.claude-haiku-5-5",
    "claude-sonnet-4-5-20250929":
      "us.anthropic.claude-sonnet-4-5-20250929-v1:0",
    "claude-sonnet-4-6": "us.anthropic.claude-sonnet-4-6",
    "claude-opus-4-5-20251101": "us.anthropic.claude-opus-4-5-20251101-v1:0",
    "claude-opus-4-6": "us.anthropic.claude-opus-4-6-v1",
    "claude-opus-4-7": "us.anthropic.claude-opus-4-7",
    "claude-opus-4-8": "us.anthropic.claude-opus-4-8",
    "claude-opus-5": "us.anthropic.claude-opus-5",
    "claude-opus-5-5": "us.anthropic.claude-opus-5-5",
    "claude-fable-5": "us.anthropic.claude-fable-5",
    "claude-fable-5-1": "us.anthropic.claude-fable-5-1",
  },
  eu: {
    "claude-sonnet-5": "eu.anthropic.claude-sonnet-5",
    "claude-sonnet-5-5": "eu.anthropic.claude-sonnet-5-5",
    "claude-haiku-5-5": "eu.anthropic.claude-haiku-5-5",
    "claude-sonnet-4-5-20250929":
      "eu.anthropic.claude-sonnet-4-5-20250929-v1:0",
    "claude-sonnet-4-6": "eu.anthropic.claude-sonnet-4-6",
    "claude-opus-4-5-20251101": "eu.anthropic.claude-opus-4-5-20251101-v1:0",
    "claude-opus-4-6": "eu.anthropic.claude-opus-4-6-v1",
    "claude-opus-4-7": "eu.anthropic.claude-opus-4-7",
    "claude-opus-4-8": "eu.anthropic.claude-opus-4-8",
    "claude-opus-5": "eu.anthropic.claude-opus-5",
    "claude-opus-5-5": "eu.anthropic.claude-opus-5-5",
    "claude-fable-5": null,
    "claude-fable-5-1": null,
  },
} as const satisfies Record<
  ActiveDeploymentRegion,
  Record<ANTHROPIC_MODEL, string | null>
>;

// `missing` is the local sentinel and uses the US profiles.
// A new DeploymentRegion fails this switch until it chooses a profile map.
const bedrockProfileRegion = (
  deploymentRegion: DeploymentRegion
): ActiveDeploymentRegion => {
  switch (deploymentRegion) {
    case "us":
    case "missing":
      return "us";
    case "eu":
      return "eu";
    default:
      return assertNever(deploymentRegion);
  }
};

export const bedrockInferenceProfileId = ({
  model,
  deploymentRegion,
}: {
  model: ANTHROPIC_MODEL;
  deploymentRegion: DeploymentRegion;
}): string => {
  const profileId =
    MODEL_NAMES_BEDROCK[bedrockProfileRegion(deploymentRegion)][model];
  if (profileId === null) {
    throw new Error(
      `No Bedrock inference profile for ${model} in ${deploymentRegion}.`
    );
  }
  return profileId;
};

export const READABLE_MODEL_NAMES: Partial<Record<LLM_MODEL, string>> = {
  "gpt-4o-2024-08-06": "GPT 4 o (2024-08)",
  "gpt-4o-2024-05-13": "GPT 4 o (2024-05)",
  "gpt-4-turbo": "GPT 4 Turbo",
  "gpt-4-turbo-2024-04-09": "GPT 4 Turbo 2024-04-09",
  "claude-sonnet-5": "Claude 5 Sonnet",
  "claude-sonnet-5-5": "Claude 5.5 Sonnet",
  "claude-haiku-5-5": "Claude 5.5 Haiku",
  "claude-sonnet-4-5-20250929": "Claude 4.5 Sonnet",
  "claude-sonnet-4-6": "Claude 4.6 Sonnet",
  "claude-opus-4-6": "Claude 4.6 Opus",
  "claude-opus-4-7": "Claude 4.7 Opus",
  "claude-opus-4-8": "Claude 4.8 Opus",
  "claude-opus-5": "Claude 5 Opus",
  "claude-opus-5-5": "Claude 5.5 Opus",
  "claude-fable-5": "Claude Fable 5",
  "claude-fable-5-1": "Claude Fable 5.1",
  "gemini-2.5-flash": "Gemini 2.5 Flash",
  "gemini-2.5-pro": "Gemini 2.5 Pro",
  "gpt-3.5": "GPT 3.5 Turbo",
  "gpt-5.5": "GPT-5.5",
  "gpt-5.4-2026-03-05": "GPT-5.4",
  "gpt-5.4-mini-2026-03-17": "GPT-5.4 mini",
  "gpt-5.4-nano-2026-03-17": "GPT-5.4 nano",
  "gpt-5.6-luna": "GPT-5.6 Luna",
  "gpt-5.6-sol": "GPT-5.6 Sol",
  "gpt-5.6-terra": "GPT-5.6 Terra",
  "gpt-6-astra": "GPT-6 Astra",
  "gpt-6.1-sol": "GPT-6.1 Sol",
  "gpt-6-luna": "GPT-6 Luna",
};

export const readableModelName = (model: LLM_MODEL): string =>
  READABLE_MODEL_NAMES[model] ?? model;

// Mirror the interview-prompt-variant flag defaults (see interviewerMessage); keep in sync.
export const DEFAULT_INTERVIEWER_MODEL =
  "claude-sonnet-4-5-20250929" satisfies STREAMABLE_MODEL;
export const DEFAULT_INTERVIEWER_PROMPT_VERSION = 12;

// Preview picker prompt versions (newest first).
export const INTERVIEWER_PROMPT_VERSIONS = [
  { value: 15, label: "v15 (2026-07): GOOD-only anti-leading examples" },
  { value: 14, label: "v14 (2026-06): Researcher persona, non-leading" },
  {
    value: 13,
    label: "v13 (2026-03): Compact motivational-interviewing prompt",
  },
  { value: 12, label: "v12 (2026-02): Turnwise split conversation" },
] as const;

// Preview picker only. READABLE_MODEL_NAMES stays complete; it also labels historical responses.
export const SELECTABLE_INTERVIEWER_MODELS: STREAMABLE_MODEL[] = [
  "claude-sonnet-5",
  "claude-sonnet-4-6",
  "claude-sonnet-4-5-20250929",
  "claude-opus-4-8",
  "claude-opus-4-7",
  "claude-fable-5",
  "gpt-5.5",
  "gpt-5.4-2026-03-05",
  "gemini-2.5-pro",
  "gemini-2.5-flash",
];

export const FIELD_IDS = {
  SUMMARY: "5umm4ry0-0000-0000-0000-000000000000",
  OTHER_REMARKS: "07132000-0000-0000-0000-000000000000",
  TAGLINE: "74611230-0000-0000-0000-000000000000",
  BULLET_SUMMARY: "b0113700-0000-0000-0000-000000000000",
  QUALITY_SCORE: "09a11700-0000-0000-0000-000000000000",
};

export const LISTEN_URL_ID_NAME = "_m";

export const ID_SEARCH_PARAMS = [
  LISTEN_URL_ID_NAME,
  "u",
  "i",
  "id",
  "name",
  "email",
  "uid",
  "uuid",
  "smpID",
];

export const SURVEY_IMG_BASE_URL = buildSupabaseURL({
  path: "/storage/v1/object/public/survey-images/",
});
export const SURVEY_DOCUMENTS_BASE_URL = buildSupabaseURL({
  path: "/storage/v1/object/public/survey-documents/",
});
export const LOGO_STORAGE_BUCKET = "logos";
export const LOGO_BASE_URL = buildSupabaseURL({
  path: `/storage/v1/object/public/${LOGO_STORAGE_BUCKET}/`,
});
const supportedVideoFileExtensions = { "video/mp4": "mp4" };
const supportedImageFileExtensions = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};
export const supportedMediaFileExtensions = {
  ...supportedImageFileExtensions,
  ...supportedVideoFileExtensions,
};
export const isSupportedMediaMimeType = (
  value: unknown
): value is keyof typeof supportedMediaFileExtensions =>
  typeof value === "string" &&
  Object.hasOwn(supportedMediaFileExtensions, value);
export const supportedVideoMimeTypes = Object.keys(
  supportedVideoFileExtensions
);
export const supportedImageMimeTypes = Object.keys(
  supportedImageFileExtensions
);
export const supportedPdfMimeTypes = ["application/pdf"];

// Mapping from language code to language name, for all possible languages
// that we can transcribe (transcription service) & translate.
//
// Controls the available languages in the UI survey editor config window -
// the ordering of this map dictates the ordering in the drop down (via the
// AudioLanguage enum which derives from AUDIO_LANGUAGES).

// AUDIO_LANGUAGES is derived from languages.py script (which merges some .csv files).
// We've commented out a few languages that we either don't want (e.g. letting
// user select from different english dialects is too much clutter), or don't
// support on the UI (e.g. Burmese, Kannada, Lao, Panjabi, Tibetan, Malayalam).
// UI. NB to support on the UI, run the translation scripts
// (2026-01-21) - sync_locales.ts and translate_privacy_policy.ts.
// We've also manually added en-medical
// (which uses nova-3-medical instead of nova-3-general deepgram model)

// AudioLanguage derives from AUDIO_LANGUAGES - so this const is the source of truth.
export const AUDIO_LANGUAGES = {
  // List popular languages (and their variants) first,
  // and then lower down alphabetically.

  en: "English",
  "en-medical": "English (Medical)",

  fr: "French",
  // We will remove at some point.
  "fr-CA": "French: fr-CA",

  de: "German",
  "de-CH": "German (Switzerland)",

  it: "Italian",

  pt: "Portuguese",
  "pt-BR": "Portuguese (Brazil)",

  es: "Spanish",

  zh: "Chinese (Mandarin, Simplified)",
  "zh-TW": "Chinese (Mandarin, Traditional)",
  "zh-HK": "Chinese (Cantonese, Traditional)",

  af: "Afrikaans",
  sq: "Albanian",
  am: "Amharic",
  ar: "Arabic",
  hy: "Armenian",
  as: "Assamese",
  ast: "Asturian",
  az: "Azerbaijani",
  ba: "Bashkir",
  eu: "Basque",
  be: "Belarusian",
  bn: "Bengali",
  bs: "Bosnian",
  br: "Breton",
  bg: "Bulgarian",
  my: "Burmese",
  ca: "Catalan",
  ceb: "Cebuano",
  ny: "Chichewa",
  hr: "Croatian",
  cs: "Czech",
  da: "Danish",
  nl: "Dutch",
  et: "Estonian",
  fo: "Faroese",
  fi: "Finnish",
  "nl-BE": "Flemish",
  ff: "Fulah",
  gl: "Galician",
  lg: "Ganda",
  ka: "Georgian",
  el: "Greek",
  gu: "Gujarati",
  ht: "Haitian Creole",
  ha: "Hausa",
  haw: "Hawaiian",
  he: "Hebrew",
  hi: "Hindi",
  hu: "Hungarian",
  is: "Icelandic",
  ig: "Igbo",
  id: "Indonesian",
  ga: "Irish",
  ja: "Japanese",
  jw: "Javanese",
  kea: "Kabuverdianu",
  kn: "Kannada",
  kk: "Kazakh",
  km: "Khmer",
  ko: "Korean",
  ku: "Kurdish",
  ky: "Kyrgyz",
  lo: "Lao",
  la: "Latin",
  lv: "Latvian",
  ln: "Lingala",
  lt: "Lithuanian",
  luo: "Luo",
  lb: "Luxembourgish",
  mk: "Macedonian",
  mg: "Malagasy",
  ms: "Malay",
  ml: "Malayalam",
  mt: "Maltese",
  mi: "Maori",
  mr: "Marathi",
  mn: "Mongolian",
  ne: "Nepali",
  nso: "Northern Sotho",
  no: "Norwegian",
  nn: "Norwegian Nynorsk",
  oc: "Occitan",
  or: "Odia",
  pa: "Panjabi",
  ps: "Pashto",
  fa: "Persian",
  pl: "Polish",
  ro: "Romanian",
  ru: "Russian",
  sa: "Sanskrit",
  sr: "Serbian",
  sn: "Shona",
  sd: "Sindhi",
  si: "Sinhala",
  sk: "Slovak",
  sl: "Slovenian",
  so: "Somali",
  su: "Sundanese",
  sw: "Swahili",
  sv: "Swedish",
  tl: "Tagalog",
  tg: "Tajik",
  ta: "Tamil",
  tt: "Tatar",
  te: "Telugu",
  th: "Thai",
  bo: "Tibetan",
  tr: "Turkish",
  tk: "Turkmen",
  uk: "Ukrainian",
  umb: "Umbundu",
  ur: "Urdu",
  uz: "Uzbek",
  vi: "Vietnamese",
  cy: "Welsh",
  wo: "Wolof",
  xh: "Xhosa",
  yi: "Yiddish",
  yo: "Yoruba",
  zu: "Zulu",
};

// The list of languages for which we have survey message translations available
// I.e. common.json and privacy_policy.md in public/locales/<language> folder.
// This should ideally be equal to, or a subset of AUDIO_LANGUAGES.
export const SUPPORTED_UI_LANGUAGES = Object.keys(
  AUDIO_LANGUAGES
) as AudioLanguage[];

export const isAudioLanguage = (code: string): code is AudioLanguage =>
  code in AUDIO_LANGUAGES;

// Narrow i18n.language (string) to AudioLanguage; unknown codes fall back to en.
export const languageFromI18n = (i18nLanguage: string): AudioLanguage =>
  isAudioLanguage(i18nLanguage) ? i18nLanguage : "en";

// Languages soft-retired from new selections. They remain valid AudioLanguage
// values so legacy persisted study configs still parse, but pickers and
// LLM-driven write paths filter them out via SELECTABLE_AUDIO_LANGUAGE_CODES.
export const HIDDEN_AUDIO_LANGUAGES = new Set<AudioLanguage>(["fr-CA"]);

export const SELECTABLE_AUDIO_LANGUAGE_CODES = (
  Object.keys(AUDIO_LANGUAGES) as AudioLanguage[]
).filter((code) => !HIDDEN_AUDIO_LANGUAGES.has(code));

// Speech-to-text only: variants transcribed as their base language. Kept
// separate from HIDDEN_AUDIO_LANGUAGES (hiding and aliasing are distinct
// decisions). i18n locales and TTS voices still use the variant (e.g. pt-BR
// keeps Brazilian-accent voices).
export const TRANSCRIPTION_AUDIO_LANGUAGE_ALIASES: Partial<
  Record<AudioLanguage, AudioLanguage>
> = {
  "fr-CA": "fr",
  "pt-BR": "pt",
};

export const REPORT_ITEM_IDS = {
  HIGHLIGHTS_HEADLINE: "highlights-headline",
  QUESTIONS_HEADLINE: "questions-headline",
  CONCEPT_COMPARISON_HEADLINE: "concept-comparison-headline",
  EXTERNAL_HEADLINE: "external-headline",
  CHARTS_HEADLINE: "charts-headline",
  HIGHLIGHT_CHARTS_HEADLINE: "highlight-charts-headline",
};

export const AUTOMATIC_ANALYSIS_RESPONSE_THRESHOLDS = [
  1, 5, 10, 20, 50, 100, 150, 200, 250, 300, 400, 500, 750, 1000,
];

export const COMPLETED_RESPONSE_THRESHOLDS = [5];

const failIfNotInAnalysisResponseThesholds = (
  thresholds: number[],
  name: string
) => {
  if (
    thresholds.some((x) => !AUTOMATIC_ANALYSIS_RESPONSE_THRESHOLDS.includes(x))
  ) {
    throw new Error(
      `${name} must be subset of AUTOMATIC_ANALYSIS_RESPONSE_THRESHOLDS`
    );
  }
};
export const AUTOMATIC_HIGHLIGHTS_RESPONSE_THRESHOLDS = [
  5, 10, 20, 50, 100, 1000,
];
failIfNotInAnalysisResponseThesholds(
  AUTOMATIC_HIGHLIGHTS_RESPONSE_THRESHOLDS,
  "AUTOMATIC_HIGHLIGHTS_RESPONSE_THRESHOLDS"
);
export const AUTOMATIC_PRESENTATION_GENERATION_RESPONSE_THRESHOLDS = [
  5, 20, 100, 1000,
];
failIfNotInAnalysisResponseThesholds(
  AUTOMATIC_PRESENTATION_GENERATION_RESPONSE_THRESHOLDS,
  "AUTOMATIC_PRESENTATION_GENERATION_RESPONSE_THRESHOLDS"
);
export const AUTOMATIC_PERSONAS_RESPONSE_THRESHOLDS = [10, 20, 50, 100, 1000];
failIfNotInAnalysisResponseThesholds(
  AUTOMATIC_PERSONAS_RESPONSE_THRESHOLDS,
  "AUTOMATIC_PERSONAS_RESPONSE_THRESHOLDS"
);
export const AUTOMATIC_SEGMENTS_RESPONSE_THRESHOLDS = [10];
failIfNotInAnalysisResponseThesholds(
  AUTOMATIC_SEGMENTS_RESPONSE_THRESHOLDS,
  "AUTOMATIC_SEGMENTS_RESPONSE_THRESHOLDS"
);

export const ANALYSIS_CHAT_MAX_RESPONSES_RETRIEVED = 20;
export const CUSTOM_CLIP_MAX_RESPONSES_RETRIEVED = 50;

export const redToGreenPalette = [
  "#EB2300",
  "#F5632E",
  "#E2CD65",
  "#8AAF46",
  "#328F24",
];

export const redGreyBluePalette = [
  "E8546F",
  "F7ABB7",
  "B3B7B8",
  "61AAFB",
  "2563EB",
];

/** Okabe-Ito colorblind-safe categorical palette, reordered brand-blue-first; use one per series directly, not interpolated. */
export const okabeItoPalette = [
  "#0072B2", // blue
  "#E69F00", // orange
  "#009E73", // bluish green
  "#CC79A7", // reddish purple
  "#56B4E9", // sky blue
  "#D55E00", // vermillion
  "#F0E442", // yellow
  "#000000", // black
];

/** Series colors for report charts: brand blues, with orange for contrast. */
export const chartSeriesPalette = [
  "#0021CC", // brand blue
  "#2E7CF6", // medium blue
  "#F3880E", // orange
  "#9DAEFF", // periwinkle
  "#F8B866", // sand
  "#031C9B", // navy
];

// Blend a #RRGGBB color toward white by t (0 = unchanged, 1 = white).
const mixHexToWhite = (hex: string, t: number): string => {
  const n = parseInt(hex.slice(1), 16);
  const channel = (shift: number) =>
    Math.round(((n >> shift) & 0xff) + (255 - ((n >> shift) & 0xff)) * t)
      .toString(16)
      .padStart(2, "0");
  return `#${channel(16)}${channel(8)}${channel(0)}`;
};

// Stable categorical color for index i; mixes toward white each palette wrap so colors stay distinct past the palette's length.
export const categoricalColor = (
  i: number,
  palette: readonly string[] = okabeItoPalette
): string => {
  const base = palette[i % palette.length];
  const cycle = Math.floor(i / palette.length);
  return cycle <= 0 ? base : mixHexToWhite(base, Math.min(0.6, cycle * 0.22));
};

export const DB_ERROR_CODES = {
  UNIQUENESS_VIOLATION: "23505",
  SINGLE_ROW_MISMATCH_ERROR: "PGRST116",
} as const;

export type DbErrorCode = keyof typeof DB_ERROR_CODES;

export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const EMAIL_REGEX_ALL =
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

export const NAVBAR_TAB_HEIGHT = 50; // px

// When switching tabs, Next.js cached pages resolve nearly instantly. Without a
// debounce the loading indicator (BarLoader / skeleton) flashes for a single
// frame, which feels janky. This delay suppresses the indicator for fast
// navigations while still showing it for genuinely slow ones.
export const NAV_LOADING_DEBOUNCE_MS = 150;
